import { useState, useEffect } from 'react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { formatPrice, calculatePricing, getDeliveryTimeframe } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Truck, Tag, Gift, X, Calendar, ArrowLeft, CheckCircle, Shield, Star, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Checkout() {
  const { cart, getTotal, clearCart } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 2-step checkout
  const [deliveryLocation, setDeliveryLocation] = useState('Mumbai');
  const [notes, setNotes] = useState('');
  
  // Coupon State
  const [showCouponPopup, setShowCouponPopup] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [eligibleCoupon, setEligibleCoupon] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [policyPopup, setPolicyPopup] = useState(null);
  
  // Simplified Buyer Details - Only essential fields
  const [buyerDetails, setBuyerDetails] = useState({
    name: user?.name || '',
    email: user?.email || '',
    contactNumber: '',
    deliveryAddress: '',
    gstNumber: '', // Optional
    billingAddress: '', // Will default to delivery address
    alternateNumber: '' // Optional
  });

  const subtotal = getTotal();
  const pricing = calculatePricing(subtotal);
  
  // Calculate product savings (MRP - Sale Price for all items)
  const productSavings = cart.reduce((total, item) => {
    const mrp = item.product.mrp || item.product.price;
    const salePrice = item.product.price;
    const savings = (mrp - salePrice) * item.quantity;
    return total + savings;
  }, 0);

  // Calculate coupon discount
  const couponDiscount = appliedCoupon ? (subtotal * appliedCoupon.discount_percent / 100) : 0;
  const finalTotal = pricing.subtotal + pricing.cgst + pricing.sgst - couponDiscount;
  
  // Total savings = Product discount + Coupon discount
  const totalSavings = productSavings + couponDiscount;

  // Check coupon eligibility on mount
  useEffect(() => {
    const checkCouponEligibility = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API_URL}/coupon/check-eligibility`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.eligible) {
          setEligibleCoupon(response.data);
          setShowCouponPopup(true);
        }
      } catch (error) {
        console.log('Coupon check failed:', error);
      }
    };
    
    checkCouponEligibility();
  }, [token]);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    try {
      const response = await axios.post(`${API_URL}/coupon/validate`, 
        { code: couponInput.toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.valid) {
        setAppliedCoupon({
          code: couponInput.toUpperCase(),
          discount_percent: response.data.discount_percent
        });
        toast.success(`Coupon applied! ${response.data.discount_percent}% discount`);
        setCouponInput('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    toast.info('Coupon removed');
  };

  const handleUseCouponFromPopup = () => {
    if (eligibleCoupon) {
      setCouponInput(eligibleCoupon.coupon_code);
      setShowCouponPopup(false);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!buyerDetails.name || !buyerDetails.contactNumber || !buyerDetails.email || !buyerDetails.deliveryAddress) {
      toast.error('Please fill all required fields');
      setStep(1);
      return;
    }
    
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setLoading(true);

    try {
      // If coupon is applied and user is logged in, mark it as used
      if (appliedCoupon && token) {
        await axios.post(`${API_URL}/coupon/use`, 
          { code: appliedCoupon.code },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      }

      const orderData = {
        items: cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          // Include product details for accessory items
          product_name: item.product.category === 'Accessory' ? item.product.name : null,
          product_price: item.product.category === 'Accessory' ? item.product.price : null,
          product_category: item.product.category === 'Accessory' ? item.product.category : null
        })),
        delivery_location: deliveryLocation,
        notes: notes || null,
        buyer_details: {
          name: buyerDetails.name,
          email: buyerDetails.email,
          contact_number: buyerDetails.contactNumber,
          alternate_number: buyerDetails.alternateNumber || null,
          gst_number: buyerDetails.gstNumber || null,
          billing_address: buyerDetails.billingAddress || buyerDetails.deliveryAddress,
          delivery_address: buyerDetails.deliveryAddress
        },
        is_guest: !user // Flag for guest orders
      };

      // Use guest endpoint if not logged in, otherwise use authenticated endpoint
      const endpoint = user ? `${API_URL}/orders` : `${API_URL}/orders/guest`;
      const headers = user ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(endpoint, orderData, { headers });

      toast.success('Order created successfully!');
      
      // Navigate to payment page
      navigate(`/payment/${response.data.id}`, { replace: true });
      
      // Clear cart after a small delay to ensure navigation completes
      setTimeout(() => {
        clearCart();
      }, 100);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
      setLoading(false);
    }
  };

  // Allow checkout without login (guest checkout enabled)
  // Only redirect to cart if cart is empty
  if (cart.length === 0 && !loading) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-12">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/cart')}
          variant="ghost"
          className="mb-6 rounded-none uppercase tracking-wider font-bold hover:bg-accent/10"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Cart
        </Button>

        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase mb-4">CHECKOUT</h1>

        {/* Guest Checkout Banner */}
        {!user && (
          <div className="flex flex-wrap items-center justify-center gap-4 mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-blue-700">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs sm:text-sm font-semibold">Guest Checkout - No account needed!</span>
            </div>
            <Link to="/login?redirect=/checkout" className="text-xs sm:text-sm text-blue-600 underline hover:text-blue-800">
              Login for faster checkout
            </Link>
          </div>
        )}

        {/* 2-Step Progress Indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
            </div>
            <span className="font-semibold text-sm hidden sm:block">Contact & Delivery</span>
          </div>
          <div className="w-12 h-0.5 bg-muted">
            <div className={`h-full bg-primary transition-all ${step > 1 ? 'w-full' : 'w-0'}`} />
          </div>
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              2
            </div>
            <span className="font-semibold text-sm hidden sm:block">Review & Pay</span>
          </div>
        </div>

        {/* Trust Signals Bar */}
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700">
            <Shield className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-semibold">Secure Checkout</span>
          </div>
          <div className="flex items-center gap-2 text-green-700">
            <Truck className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-semibold">Free Delivery (Mumbai)</span>
          </div>
          <div className="flex items-center gap-2 text-green-700">
            <Star className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-semibold">5-Year Warranty</span>
          </div>
        </div>

        <form onSubmit={handleSubmitOrder}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Step 1: Contact & Delivery OR Step 2: Review */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* STEP 1: Contact & Delivery */}
              {step === 1 && (
                <>
                  {/* Essential Contact Details */}
                  <div className="bg-white border border-border rounded-none p-6 sm:p-8">
                    <h2 className="text-xl sm:text-2xl font-black uppercase mb-6">CONTACT DETAILS</h2>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="uppercase tracking-wider text-xs font-bold">Name *</Label>
                          <Input
                            value={buyerDetails.name}
                            onChange={(e) => setBuyerDetails({...buyerDetails, name: e.target.value})}
                            data-testid="buyer-name"
                            required
                            placeholder="Your full name"
                            className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                          />
                        </div>
                        <div>
                          <Label className="uppercase tracking-wider text-xs font-bold">Phone *</Label>
                          <Input
                            type="tel"
                            value={buyerDetails.contactNumber}
                            onChange={(e) => setBuyerDetails({...buyerDetails, contactNumber: e.target.value})}
                            data-testid="buyer-contact"
                            required
                            placeholder="10-digit mobile"
                            className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="uppercase tracking-wider text-xs font-bold">Email *</Label>
                        <Input
                          type="email"
                          value={buyerDetails.email}
                          onChange={(e) => setBuyerDetails({...buyerDetails, email: e.target.value})}
                          data-testid="buyer-email"
                          required
                          placeholder="your@email.com"
                          className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Delivery Address */}
                  <div className="bg-white border border-border rounded-none p-6 sm:p-8">
                    <h2 className="text-xl sm:text-2xl font-black uppercase mb-6">DELIVERY ADDRESS</h2>
                    <div className="space-y-4">
                      <div>
                        <Label className="uppercase tracking-wider text-xs font-bold">Complete Address *</Label>
                        <Textarea
                          value={buyerDetails.deliveryAddress}
                          onChange={(e) => setBuyerDetails({...buyerDetails, deliveryAddress: e.target.value})}
                          data-testid="buyer-delivery-address"
                          required
                          placeholder="Building, Street, Area, City, Pincode"
                          className="bg-transparent border-2 border-border focus:border-primary rounded-none p-4 min-h-[100px]"
                        />
                      </div>
                      
                      <div>
                        <Label className="uppercase tracking-wider text-xs font-bold">Delivery Location</Label>
                        <select
                          value={deliveryLocation}
                          onChange={(e) => setDeliveryLocation(e.target.value)}
                          data-testid="delivery-location"
                          className="w-full bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4 focus:ring-0 transition-colors"
                        >
                          <option value="Mumbai">Mumbai / Navi Mumbai (FREE Delivery)</option>
                          <option value="Other">Other Cities (Shipping Extra)</option>
                        </select>
                      </div>

                      {/* Optional: GST for Business */}
                      <div className="pt-4 border-t border-border">
                        <Label className="uppercase tracking-wider text-xs font-bold text-muted-foreground">GST Number (Optional - For Business)</Label>
                        <Input
                          value={buyerDetails.gstNumber}
                          onChange={(e) => setBuyerDetails({...buyerDetails, gstNumber: e.target.value})}
                          data-testid="buyer-gst"
                          placeholder="Enter GST if you need GST invoice"
                          className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Continue to Step 2 Button */}
                  <Button
                    type="button"
                    onClick={() => {
                      if (!buyerDetails.name || !buyerDetails.contactNumber || !buyerDetails.email || !buyerDetails.deliveryAddress) {
                        toast.error('Please fill all required fields');
                        return;
                      }
                      // Set billing address same as delivery if not set
                      if (!buyerDetails.billingAddress) {
                        setBuyerDetails(prev => ({...prev, billingAddress: prev.deliveryAddress}));
                      }
                      setStep(2);
                      window.scrollTo(0, 0);
                    }}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold py-6 flex items-center justify-center gap-2"
                  >
                    Continue to Review
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </>
              )}

              {/* STEP 2: Review Order */}
              {step === 2 && (
                <>
                  {/* Back Button */}
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="ghost"
                    className="mb-4 rounded-none uppercase tracking-wider font-bold hover:bg-accent/10"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Edit Details
                  </Button>

                  {/* Order Items Review */}
                  <div className="bg-white border border-border rounded-none p-6 sm:p-8">
                    <h2 className="text-xl sm:text-2xl font-black uppercase mb-6">ORDER ITEMS ({cart.length})</h2>
                    <div className="space-y-4">
                      {cart.map((item) => (
                        <div key={item.product.id} className="flex gap-4 border-b border-border pb-4">
                          <img 
                            src={item.product.image_url} 
                            alt={item.product.name}
                            className="w-16 h-16 object-cover border"
                          />
                          <div className="flex-1">
                            <p className="font-bold uppercase text-sm">{item.product.name}</p>
                            <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                          </div>
                          <p className="mono font-bold">{formatPrice(item.product.price * item.quantity)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Delivery Summary */}
                  <div className="bg-white border border-border rounded-none p-6 sm:p-8">
                    <h2 className="text-xl sm:text-2xl font-black uppercase mb-4">DELIVERING TO</h2>
                    <div className="space-y-2 text-sm">
                      <p className="font-bold">{buyerDetails.name}</p>
                      <p>{buyerDetails.deliveryAddress}</p>
                      <p>{buyerDetails.contactNumber} • {buyerDetails.email}</p>
                      {buyerDetails.gstNumber && <p className="text-muted-foreground">GST: {buyerDetails.gstNumber}</p>}
                    </div>
                  </div>

                  {/* Special Instructions */}
                  <div className="bg-white border border-border rounded-none p-6 sm:p-8">
                    <Label className="uppercase tracking-wider text-xs font-bold">Special Instructions (Optional)</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      data-testid="order-notes"
                      placeholder="Any special delivery instructions..."
                      className="bg-transparent border-2 border-border focus:border-primary rounded-none p-4 min-h-[80px] mt-2"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Order Summary - Compact on Step 1, Full on Step 2 */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-border rounded-none p-6 sm:p-8 sticky top-24">
                <h2 className="text-xl sm:text-2xl font-black uppercase mb-4">ORDER SUMMARY</h2>

                {/* Compact Product List */}
                <div className="space-y-2 mb-4 pb-4 border-b border-border max-h-[200px] overflow-y-auto">
                  {cart.map((item) => (
                    <div key={item.product.id} className="flex justify-between items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-bold uppercase leading-tight truncate">{item.product.name}</p>
                        <p className="text-xs text-muted-foreground">× {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        {item.product.mrp && item.product.mrp > item.product.price && (
                          <p className="mono text-xs text-muted-foreground line-through">{formatPrice(item.product.mrp * item.quantity)}</p>
                        )}
                        <p className="mono text-xs sm:text-sm font-bold whitespace-nowrap">{formatPrice(item.product.price * item.quantity)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Country of Origin - Single line */}
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border">
                  <img 
                    src="https://flagcdn.com/w20/in.png" 
                    alt="India"
                    className="w-5 h-3 object-cover rounded-sm"
                  />
                  <span className="text-sm font-semibold text-gray-700">Country of Origin: India</span>
                </div>

                <div className="space-y-3 mono text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-bold">{formatPrice(pricing.subtotal)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>CGST (9%):</span>
                    <span className="font-bold">{formatPrice(pricing.cgst)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>SGST (9%):</span>
                    <span className="font-bold">{formatPrice(pricing.sgst)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Shipping:</span>
                    {deliveryLocation === 'Mumbai' ? (
                      <span className="font-bold text-green-600">FREE</span>
                    ) : (
                      <span className="font-bold text-orange-600">To be Paid by Buyer</span>
                    )}
                  </div>

                  {appliedCoupon && (
                    <div className="flex justify-between text-green-600">
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        Coupon ({appliedCoupon.discount_percent}%):
                      </span>
                      <span className="font-bold">-{formatPrice(couponDiscount)}</span>
                    </div>
                  )}

                  <div className="border-t border-border pt-3 mt-3">
                    <div className="flex justify-between text-lg">
                      <span className="font-bold">TOTAL:</span>
                      <span className="font-black" data-testid="checkout-total">
                        {formatPrice(finalTotal)}
                      </span>
                    </div>
                    
                    {totalSavings > 0 && (
                      <div className="flex justify-between mt-3 bg-green-100 p-3 border-2 border-green-500">
                        <span className="text-green-700 font-black text-sm flex items-center gap-1">
                          🎉 Total Amount Saved:
                        </span>
                        <span className="text-green-700 font-black text-lg">{formatPrice(totalSavings)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Coupon Code Section */}
                <div className="mt-6 p-4 bg-muted/30 border border-border rounded-none">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4 text-primary" />
                    <p className="text-sm font-bold uppercase">Apply Coupon</p>
                  </div>
                  
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 p-3">
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-green-600" />
                        <span className="font-bold text-green-600">{appliedCoupon.code}</span>
                        <span className="text-xs text-green-600">({appliedCoupon.discount_percent}% off)</span>
                      </div>
                      <button 
                        onClick={handleRemoveCoupon}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="flex-1 rounded-none uppercase"
                      />
                      <Button
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading}
                        className="rounded-none bg-primary text-primary-foreground"
                      >
                        {couponLoading ? '...' : 'Apply'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Delivery Timeframe for this Order */}
                <div className="mt-6 p-4 bg-accent border-2 border-accent rounded-none">
                  <div className="flex items-center gap-2 mb-3">
                    <Truck className="w-5 h-5 text-primary" />
                    <p className="text-sm font-black uppercase text-primary">Expected Delivery</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Clock className="w-6 h-6 text-foreground" />
                      <p className="text-3xl font-black text-foreground mono">
                        {getDeliveryTimeframe(subtotal)}
                      </p>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-foreground">
                      <Calendar className="w-4 h-4" />
                      <p className="text-sm font-semibold">
                        Expected by {(() => {
                          const days = parseInt(getDeliveryTimeframe(subtotal));
                          const date = new Date();
                          date.setDate(date.getDate() + days);
                          return date.toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          });
                        })()}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-center text-foreground mt-2 italic">
                    * May be reduced if items are in stock
                  </p>
                </div>

                {/* Terms & Policies Checkbox */}
                <div className="mt-6 p-4 bg-secondary border border-border">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded-none border-2 border-gray-400 cursor-pointer accent-primary"
                    />
                    <span className="text-sm text-muted-foreground leading-relaxed">
                      I have reviewed my order details and understand the{' '}
                      <button 
                        type="button"
                        onClick={() => setPolicyPopup('terms')}
                        className="text-primary hover:underline font-medium"
                      >
                        Terms & Policies
                      </button>
                      {' '}herein.
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !termsAccepted}
                  data-testid="place-order-button"
                  className="w-full mt-6 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground disabled:bg-muted disabled:text-muted-foreground rounded-none uppercase tracking-wider font-bold py-6"
                >
                  {loading ? 'PROCESSING...' : 'PLACE ORDER'}
                </Button>

                {!termsAccepted && (
                  <p className="text-xs text-center text-amber-600 mt-2">
                    Please accept the terms & policies to proceed
                  </p>
                )}

                {deliveryLocation === 'Other' && (
                  <p className="text-xs text-center text-amber-600 mt-4 font-medium">
                    ⚠️ Delivery charges to be paid separately by buyer
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>

        {/* First Purchase Coupon Popup */}
        <Dialog open={showCouponPopup} onOpenChange={setShowCouponPopup}>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black uppercase flex items-center gap-2">
                <Gift className="w-6 h-6 text-primary" />
                Welcome Gift!
              </DialogTitle>
              <DialogDescription className="text-base mt-4">
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-primary/10 to-accent/10 p-6 border-2 border-primary">
                    <p className="text-lg font-bold text-foreground mb-2">
                      🎉 First Purchase Discount!
                    </p>
                    <p className="text-foreground mb-4">
                      As a new customer, you&apos;re eligible for a special <span className="font-black text-primary">2% discount</span> on your first order!
                    </p>
                    <div className="bg-white p-4 border border-border">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Your Coupon Code</p>
                      <p className="text-2xl font-black text-primary mono tracking-wider">
                        {eligibleCoupon?.coupon_code}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      onClick={handleUseCouponFromPopup}
                      className="flex-1 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground rounded-none uppercase tracking-wider font-bold"
                    >
                      Use This Code
                    </Button>
                    <Button 
                      onClick={() => setShowCouponPopup(false)} 
                      variant="outline" 
                      className="flex-1 rounded-none uppercase tracking-wider font-bold"
                    >
                      Maybe Later
                    </Button>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>

        {/* Terms & Policies Popup */}
        <Dialog open={policyPopup !== null} onOpenChange={() => setPolicyPopup(null)}>
          <DialogContent className="rounded-none max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="text-2xl font-black uppercase">
                Terms & Policies
              </DialogTitle>
              <DialogDescription className="sr-only">
                Review our terms and policies
              </DialogDescription>
            </DialogHeader>
            
            <div className="overflow-y-auto flex-1 pr-2 -mr-2">
              {/* Quick Navigation */}
              <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-border sticky top-0 bg-white">
                <button
                  type="button"
                  onClick={() => document.getElementById('popup-shipping')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-xs px-3 py-1 bg-secondary hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  Shipping
                </button>
                <button
                  type="button"
                  onClick={() => document.getElementById('popup-privacy')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-xs px-3 py-1 bg-secondary hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  Privacy
                </button>
                <button
                  type="button"
                  onClick={() => document.getElementById('popup-cancellation')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-xs px-3 py-1 bg-secondary hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  Cancellation & Refund
                </button>
                <button
                  type="button"
                  onClick={() => document.getElementById('popup-terms')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-xs px-3 py-1 bg-secondary hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  Terms & Conditions
                </button>
              </div>

              <div className="space-y-8 text-sm">
                {/* Shipping Policy */}
                <section id="popup-shipping" className="scroll-mt-16">
                  <h3 className="text-lg font-black uppercase mb-2">Shipping & Delivery Policy</h3>
                  <p className="text-xs text-muted-foreground mb-3">Last updated: Dec 25, 2025</p>
                  <div className="text-muted-foreground space-y-2">
                    <p>Orders are shipped within 0-7 days or as per the delivery date agreed at the time of order confirmation. For domestic buyers, orders are shipped through registered domestic courier companies and/or speed post only.</p>
                    <p>MODULAR FURNITURE STORE is not liable for any delay in delivery by the courier company/postal authorities. Delivery of all orders will be to the address provided by the buyer.</p>
                    <p>Contact: <strong>9820596811</strong> or <strong>hello@modularfurniturestore.com</strong></p>
                  </div>
                </section>

                {/* Privacy Policy */}
                <section id="popup-privacy" className="scroll-mt-16">
                  <h3 className="text-lg font-black uppercase mb-2">Privacy Policy</h3>
                  <p className="text-xs text-muted-foreground mb-3">Last updated: Dec 25, 2025</p>
                  <div className="text-muted-foreground space-y-2">
                    <p>MODULAR FURNITURE STORE is committed to ensuring that your privacy is protected. We may collect: Name, Contact information, Email address, and other information relevant to customer surveys and offers.</p>
                    <p>We use this information for internal record keeping, improving our products and services, and sending promotional emails about new products and special offers.</p>
                    <p>We will not sell, distribute or lease your personal information to third parties unless required by law.</p>
                  </div>
                </section>

                {/* Cancellation & Refund */}
                <section id="popup-cancellation" className="scroll-mt-16">
                  <h3 className="text-lg font-black uppercase mb-2">Cancellation & Refund Policy</h3>
                  <p className="text-xs text-muted-foreground mb-3">Last updated: Dec 25, 2025</p>
                  <div className="text-muted-foreground space-y-2">
                    <p>We have implemented a &quot;No Cancellation or Refund&quot; policy once an order is placed.</p>
                    <p><strong>Immediate Processing:</strong> Orders are automatically sent to production upon confirmation.</p>
                    <p><strong>Inventory Commitment:</strong> Items are immediately reserved for you.</p>
                    <p><strong>Resource Allocation:</strong> Labor and materials are committed upon finalization.</p>
                    <p>Please double-check product specifications, shipping details, and compatibility before placing your order.</p>
                  </div>
                </section>

                {/* Terms & Conditions */}
                <section id="popup-terms" className="scroll-mt-16">
                  <h3 className="text-lg font-black uppercase mb-2">Terms & Conditions</h3>
                  <p className="text-xs text-muted-foreground mb-3">Last updated: Dec 25, 2025</p>
                  <div className="text-muted-foreground space-y-2">
                    <p>MODULAR FURNITURE STORE is located in Mumbai City, Maharashtra, India.</p>
                    <p>The content of this website is subject to change without notice. We do not provide any warranty or guarantee as to the accuracy of information on this website.</p>
                    <p>Your use of any information or materials on our website is entirely at your own risk. Unauthorized use of information provided by us shall give rise to a claim for damages.</p>
                    <p>Any dispute arising out of use of our website and/or purchase with us is subject to the laws of India.</p>
                  </div>
                </section>

                {/* Contact */}
                <section className="bg-secondary p-4">
                  <h3 className="text-sm font-bold uppercase mb-2">Contact Us</h3>
                  <p className="text-muted-foreground text-xs">
                    Merchant: MODULAR FURNITURE STORE | Tel: 9820596811 | Email: hello@modularfurniturestore.com
                  </p>
                </section>
              </div>
            </div>

            <div className="flex-shrink-0 pt-4 border-t border-border mt-4">
              <Button 
                onClick={() => setPolicyPopup(null)} 
                className="w-full rounded-none uppercase tracking-wider font-bold"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
